import { atomWithStorage } from 'jotai/utils';
import { getMapDefaultConfig } from 'components/map/atoms';
import { atomWithLoadable, mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import { toastAtom as sharedToastAtom } from 'components/scenario-area-configs/atoms';
import type {
  DifferentialScenarioResponse,
  EnumsResponse,
  SaveScenarioParams,
} from './types';

// ─────────────────────────────────────
// Re-export shared area config atoms
// ─────────────────────────────────────

export { sharedToastAtom as toastAtom };

export {
  categoriesBaseAtom,
  categoriesLoadableAtom,
  unitTypesBaseAtom,
  unitTypesLoadableAtom,
  areaCategoryAtom,
  areaQueryAtom,
  areaSearchLoadableAtom,
  unitTypeAtom,
  unitQueryAtom,
  unitSearchLoadableAtom,
  wellQueryAtom,
  wellSearchLoadableAtom,
  areaGeometriesAtom,
  fetchGeometryAtom,
  fetchGeometryLoadableAtom,
} from 'components/scenario-area-configs/atoms';

// ─────────────────────────────────────
// Map config — scoped to the differentials feature
// ─────────────────────────────────────

export const differentialMapConfigAtom = atomWithStorage(
  'insights-differential-scenario-map-config',
  getMapDefaultConfig({
    wellsV2: {
      checked: false,
      options: {
        surfaceHole: false,
        bottomHole: false,
        stick: false,
        survey: false,
        vertical: false,
        horizontal: false,
        horizontalPermit: false,
        horizontalDuc: false,
        horizontalProducing: false,
        horizontalShutIn: false,
        horizontalExpiredPermit: false,
        horizontalInactive: false,
        horizontalPrePermit: false,
        horizontalApd: false,
        horizontalProposal: false,
        horizontalPlanned: false,
        verticalPermit: false,
        verticalDuc: false,
        verticalProducing: false,
        verticalShutIn: false,
        verticalExpiredPermit: false,
        verticalInactive: false,
        verticalPrePermit: false,
        verticalApd: false,
        verticalProposal: false,
        verticalPlanned: false,
        permit: false,
        duc: false,
        producing: false,
        shutIn: false,
        expiredPermit: false,
        inactive: false,
        prePermit: false,
        apd: false,
        proposal: false,
        planned: false,
      },
    },
    developmentAreas: {
      checked: false,
      options: {
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
    basinTier: {
      checked: false,
      options: {
        basin: false,
        tier: false,
      },
    },
    titleEntityAssets: {
      checked: false,
      options: {
        surfaceInterest: false,
        workingInterest: false,
        unleased: false,
        royaltyInterest: false,
      },
    },
    scrollZoom: {
      checked: false,
    },
    popups: {
      checked: false,
    },
  })
);

// ─────────────────────────────────────
// Enums (products / value types / profiles / escalation / tail)
// ─────────────────────────────────────

/**
 * Fetches all differential line-item enum values for dropdown population.
 * @return {EnumsResponse} The enums response
 */
export const [enumsBaseAtom, enumsLoadableAtom] = atomWithLoadable(async () => {
  const response = await request.get<EnumsResponse>(
    '/differential_scenarios/enums'
  );
  return response as EnumsResponse;
});

// ─────────────────────────────────────
// Save scenario (create POST / update PATCH)
// ─────────────────────────────────────

/**
 * Saves a differential scenario via POST (create) or PATCH (update).
 * @param {SaveScenarioParams} params - The mode, optional ID, and payload
 * @return {DifferentialScenarioResponse} The saved scenario response
 */
export const [saveScenarioAtom, saveScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (
      _get,
      _set,
      params: SaveScenarioParams
    ): Promise<DifferentialScenarioResponse> => {
      const url =
        params.mode === 'create'
          ? '/differential_scenarios'
          : `/differential_scenarios/${params.id}`;

      const method = params.mode === 'create' ? request.post : request.patch;

      const response = await method<DifferentialScenarioResponse, unknown>(
        url,
        params.payload,
        new AbortController()
      );

      if (response.type !== 'success') {
        const data = response.type === 'error' ? response.data : undefined;
        const body = data as { errors?: { message: string }[] } | undefined;
        const message =
          body?.errors?.map((e) => e.message).join(', ') ||
          'Failed to save scenario';
        throw new Error(message);
      }

      return response.data;
    }
  );

// ─────────────────────────────────────
// Clone scenario
// ─────────────────────────────────────

/**
 * Clones a differential scenario by ID.
 * @param {number} id - The scenario ID to clone
 * @return {DifferentialScenarioResponse} The cloned scenario response
 */
export const [cloneScenarioAtom, cloneScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (_get, set, id: number): Promise<DifferentialScenarioResponse> => {
      const response = await request.post<
        DifferentialScenarioResponse,
        unknown
      >(`/differential_scenarios/${id}/clone`, {}, new AbortController());

      if (response.type !== 'success') {
        set(sharedToastAtom, {
          message: 'Failed to clone scenario',
          type: 'error',
        });
        throw new Error('Failed to clone scenario');
      }

      return response.data;
    }
  );

// ─────────────────────────────────────
// Fetch scenario (edit mode)
// ─────────────────────────────────────

/**
 * Fetches a differential scenario by ID for edit mode.
 * @param {number} id - The scenario ID
 * @return {DifferentialScenarioResponse} The scenario response
 */
export const [fetchScenarioAtom, fetchScenarioLoadableAtom] =
  mutationAtomWithLoadable(
    async (_get, _set, id: number): Promise<DifferentialScenarioResponse> => {
      const response = await request.get<DifferentialScenarioResponse>(
        `/differential_scenarios/${id}`
      );

      if (!response || 'error' in response) {
        throw new Error(
          (response as { error: string })?.error ?? 'Failed to load scenario'
        );
      }

      return response;
    }
  );
